import React, { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { apiErrorCode, crmErrorMessage } from "../api/crmErrors";
import { CRM_ERRORS } from "../contract";
import { fmtBirthday, monthNames } from "../format";
import useTags from "../hooks/useTags";
import InlineEditableField from "../ui/InlineEditableField";
import CrmPopover from "../ui/CrmPopover";
import CrmSelect from "../ui/CrmSelect";
import CrmButton from "../ui/CrmButton";
import TagInput from "../ui/TagInput";
import { FOCUS_RING } from "../ui/classes";
import { cn } from "../../../lib/utils";
import StageControl from "./StageControl";
import { CRM } from "../../../constants/testIds/crm";

const EMAIL = z.string().email();
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

/** "Birthday 14 Mar" — day + month is enough (spec); editing opens a two-select picker. */
function BirthdayField({ birthday, onSave }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState(birthday?.day ? String(birthday.day) : "");
  const [month, setMonth] = useState(birthday?.month ? String(birthday.month) : "");
  const save = async (value) => { try { await onSave(value); setOpen(false); } catch { /* reported by the caller */ } };
  return (
    <CrmPopover open={open} onOpenChange={setOpen} className="w-72"
      trigger={(
        <button type="button" data-testid={CRM.clientField("birthday")} className={cn("rounded-lg hover:bg-eh-cream-deep", FOCUS_RING)}>
          {birthday?.day ? t("detail.birthday", { date: fmtBirthday(birthday, lang) }) : <span className="text-eh-muted">{t("detail.addBirthday")}</span>}
        </button>
      )}>
      <div className="flex gap-2">
        <CrmSelect aria-label={t("add.birthdayDay")} value={day} onChange={(e) => setDay(e.target.value)} placeholder={t("add.day")}
          options={DAYS.map((d) => ({ value: String(d), label: String(d) }))} />
        <CrmSelect aria-label={t("add.birthdayMonth")} value={month} onChange={(e) => setMonth(e.target.value)} placeholder={t("add.month")}
          options={monthNames(lang).map((m, i) => ({ value: String(i + 1), label: m }))} />
      </div>
      <div className="mt-3 flex justify-between">
        {birthday?.day ? <CrmButton variant="link" size="sm" onClick={() => save(null)}>{t("common.clear")}</CrmButton> : <span />}
        <CrmButton size="sm" disabled={!day || !month} onClick={() => save({ day: Number(day), month: Number(month) })}>{t("common.save")}</CrmButton>
      </div>
    </CrmPopover>
  );
}

/** The tags as outlined chips; a click edits them in place. */
function TagEditor({ tags, onSave }) {
  const t = useT("crm");
  const { tags: known } = useTags();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(tags);
  return (
    <CrmPopover open={open} onOpenChange={(o) => { setOpen(o); if (o) setDraft(tags); }} className="w-80"
      trigger={(
        <button type="button" data-testid={CRM.clientTags} aria-label={t("detail.editTags")} className={cn("inline-flex flex-wrap gap-1.5 rounded-lg", FOCUS_RING)}>
          {tags.length ? tags.map((tag) => (
            <span key={tag} className="rounded-full border border-eh-border-soft bg-eh-cream px-2.5 py-0.5 text-xs text-eh-ink">{tag}</span>
          )) : <span className="text-eh-muted">{t("detail.addTags")}</span>}
        </button>
      )}>
      <TagInput value={draft} onChange={setDraft} suggestions={known} label={t("add.tags")} placeholder={t("add.tagsPlaceholder")} />
      <div className="mt-3 flex justify-end">
        <CrmButton size="sm" onClick={async () => { try { await onSave(draft); setOpen(false); } catch { /* reported by the caller */ } }}>{t("common.save")}</CrmButton>
      </div>
    </CrmPopover>
  );
}

/**
 * Client Detail header (spec Screen 3): avatar, name, stage badge, then
 * "email · phone · Birthday 14 Mar · tags" and the caption "Click any field to edit
 * inline". Every edit is optimistic (useClient.update) and rolls back on refusal; an
 * email already used by another client is reported under the field.
 */
export default function ClientHeader({ client, update, setStage, resumeAutomatic }) {
  const t = useT("crm");
  const tc = useT("common");
  const [emailError, setEmailError] = useState("");
  const name = client.display_name || [client.first_name, client.last_name].filter(Boolean).join(" ") || client.primary_email;

  const save = (patch) => update(patch).catch((e) => { toast.error(crmErrorMessage(e, t, tc)); throw e; });
  const saveEmail = (email) => update({ email }).then(() => setEmailError("")).catch((e) => {
    const code = apiErrorCode(e);
    setEmailError(code === CRM_ERRORS.clientExists ? t("detail.emailTaken") : crmErrorMessage(e, t, tc));
    throw e;
  });
  const saveName = (full) => {
    const [first, ...rest] = full.split(/\s+/);
    return save({ first_name: first, last_name: rest.join(" ") });
  };

  return (
    <header className="flex flex-col gap-4 sm:flex-row sm:items-start">
      <span aria-hidden="true" className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full bg-eh-sage/30 font-display text-2xl text-eh-ink">
        {(client.first_name || name || "?").charAt(0).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-4xl leading-tight text-eh-ink">
            <InlineEditableField value={name} onSave={saveName} label={t("detail.editName")} testId={CRM.clientName}
              validate={(v) => (v ? "" : t("add.errNameRequired"))} inputClassName="text-xl" />
          </h1>
          <StageControl client={client} onSet={setStage} onResume={resumeAutomatic} />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-2 text-sm text-eh-ink">
          <InlineEditableField value={client.primary_email} inputType="email" onSave={saveEmail} error={emailError}
            label={t("add.email")} testId={CRM.clientField("email")}
            validate={(v) => (EMAIL.safeParse(v).success ? "" : t("add.errEmailInvalid"))} />
          <span aria-hidden="true">·</span>
          <InlineEditableField value={client.phone || ""} inputType="tel" placeholder={t("detail.addPhone")}
            onSave={(v) => save({ phone: v || null })} label={t("add.phone")} testId={CRM.clientField("phone")} />
          <span aria-hidden="true">·</span>
          <BirthdayField birthday={client.birthday} onSave={(b) => save({ birthday: b })} />
          <span aria-hidden="true">·</span>
          <TagEditor tags={client.tags || []} onSave={(tags) => save({ tags })} />
        </div>
        <p className="mt-2 text-xs text-eh-muted">{t("detail.editHint")}</p>
      </div>
    </header>
  );
}
